// @ts-nocheck
import { Component } from '@theme/component';
import {
  API_ENDPOINTS,
  STORAGE_KEYS,
  BOOKING_STEPS,
  URGENCY_VARIANT_CONFIG,
  BookingStateManager,
  ProductDataManager,
  BookingButtonManager,
  shouldSendServiceFeeCode,
  formatEmiratesId
} from '@theme/booking-state';

// Cache for nationality data to avoid repeated API calls
let nationalityCache = null;

class BookingJourney extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    // Government products always show booking journey button only (no toggle logic needed)
    // Pre-fetch nationality data for later use
    this.fetchNationalities();
  }

  disconnectedCallback() {
  }

  /**
   * Fetch nationality list from API and cache it
   * Returns array of { nationalityId, nationalityName, nationalityCode }
   */
  async fetchNationalities() {
    if (nationalityCache && nationalityCache.length > 0) return nationalityCache;

    try {
      const response = await fetch(API_ENDPOINTS.NATIONALITY);
      if (!response.ok) throw new Error('Failed to fetch nationalities');
      
      const result = await response.json();
      
      // Handle different response structures
      if (Array.isArray(result)) {
        nationalityCache = result;
      } else if (result.data && Array.isArray(result.data)) {
        nationalityCache = result.data;
      } else if (result.nationalities && Array.isArray(result.nationalities)) {
        nationalityCache = result.nationalities;
      } else if (typeof result === 'object') {
        // Try to find any array property in the response
        const arrayProp = Object.keys(result).find(key => Array.isArray(result[key]));
        if (arrayProp) {
          nationalityCache = result[arrayProp];
        } else {
          console.warn('Unexpected nationality API response format:', result);
          nationalityCache = [];
        }
      } else {
        console.warn('Unexpected nationality API response format:', result);
        nationalityCache = [];
      }
      return nationalityCache;
    } catch (error) {
      console.error('Error fetching nationalities:', error);
      return [];
    }
  }

  /**
   * Get nationality code from nationality name
   * @param {string} nationalityName - The nationality name to look up
   * @returns {string} The nationality code, or '0' if not found
   */
  async getNationalityCodeByName(nationalityName) {
    if (!nationalityName) return '0';

    const nationalities = await this.fetchNationalities();
    
    if (!Array.isArray(nationalities) || nationalities.length === 0) return '0';

    // Find by name (case-insensitive)
    const searchName = nationalityName.toUpperCase().trim();
    const found = nationalities.find(
      n => n.nationalityName?.toUpperCase().trim() === searchName
    );

    return found?.nationalityCode || '0';
  }

  applicationDetails(applicationNumber, lastName, isAlreadyEncoded = false) {
    return new Promise((resolve, reject) => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      
      // Encode application number with base64 (skip if already encoded, e.g., from mfDetails)
      const encodedApplicationNumber = isAlreadyEncoded ? applicationNumber : btoa(applicationNumber);

      const raw = JSON.stringify({
        "applicationNumber": encodedApplicationNumber,
        "lastName": lastName
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      fetch(API_ENDPOINTS.APPLICATION_DETAILS, requestOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          console.error('Application details error:', error);
          reject(error);
        });
    });
  }

  bookingSlots(appointmentDate, clinicCode, serviceCodes = []) {
    return new Promise((resolve, reject) => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        "appointmentDate": appointmentDate,
        "clinicCode": clinicCode,
        "availedServices": serviceCodes
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      fetch(API_ENDPOINTS.BOOKING_SLOTS, requestOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          console.error('Booking slots error:', error);
          reject(error);
        });
    });
  }

  mfDetails(lastName, emiratesIDNumber = '', passportNumber = '') {
    return new Promise((resolve, reject) => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      // Encode passport and emirates ID with base64 if not empty
      // The API expects the dashed Emirates ID format, so normalize before encoding
      const normalizedEmiratesId = formatEmiratesId(emiratesIDNumber);
      const encodedEmiratesId = normalizedEmiratesId ? btoa(normalizedEmiratesId) : '';
      const encodedPassport = passportNumber ? btoa(passportNumber) : '';

      const raw = JSON.stringify({
        "emiratesIDNumber": encodedEmiratesId,
        "passportNumber": encodedPassport,
        "lastName": lastName
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      fetch(API_ENDPOINTS.MF_DETAILS, requestOptions)
        .then((response) => response.json())
        .then((result) => {
          // Check if applicationNumber is null - treat as error
          if (!result.applicationNumber) {
            reject(new Error('Application not found'));
            return;
          }
          
          resolve(result);
        })
        .catch((error) => {
          console.error('mfDetails error:', error);
          reject(error);
        });
    });
  }

  saveBooking() {
    return new Promise(async (resolve, reject) => {
      // Get customer data from localStorage (key is 'customerData' from cart-booking-journey.js)
      const customerDataStr = localStorage.getItem('customerData');
      const bookingDataStr = localStorage.getItem('booking_data');
      const productDataStr = localStorage.getItem('booking_product');
      
      if (!customerDataStr || !bookingDataStr) {
        reject(new Error('Missing booking or customer data'));
        return;
      }
      
      const customerData = JSON.parse(customerDataStr);
      const bookingData = JSON.parse(bookingDataStr);
      const productData = productDataStr ? JSON.parse(productDataStr) : {};
      
      // Format date of birth to ISO string
      const dobDate = customerData.dateOfBirth ? new Date(customerData.dateOfBirth) : null;
      const dobISO = dobDate ? dobDate.toISOString() : '';
      
      // Format appointment date with time to ISO string
      // Combine date (e.g., "2026-01-22") with time text (e.g., "12:00 PM")
      let appointmentDateISO = '';
      if (bookingData.appointmentDate) {
        const dateStr = bookingData.appointmentDate;
        const timeText = bookingData.appointmentTimeText || '';
        
        // Parse time text (e.g., "12:00 PM" or "09:30 AM")
        let hours = 0;
        let minutes = 0;
        
        if (timeText) {
          const timeMatch = timeText.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
          if (timeMatch) {
            hours = parseInt(timeMatch[1], 10);
            minutes = parseInt(timeMatch[2], 10);
            const period = timeMatch[3]?.toUpperCase();
            
            // Convert to 24-hour format
            if (period === 'PM' && hours !== 12) {
              hours += 12;
            } else if (period === 'AM' && hours === 12) {
              hours = 0;
            }
          }
        }
        
        // Create date with time
        const appointmentDateTime = new Date(dateStr);
        appointmentDateTime.setHours(hours, minutes, 0, 0);
        appointmentDateISO = appointmentDateTime.toISOString();
      }
      
      // Encode passport and emirates ID in base64
      // API only accepts one - if passport exists, leave emirates ID blank
      // The API expects the dashed Emirates ID format; customerData can hold an
      // undashed value when it came from the API or older saved state
      const passportEncoded = customerData.passportNumber ? btoa(customerData.passportNumber) : '';
      const normalizedEmiratesId = formatEmiratesId(customerData.emirateIDNumber);
      const emiratesIdEncoded = passportEncoded ? '' : (normalizedEmiratesId ? btoa(normalizedEmiratesId) : '');
      
      // Get service types from all cart items via line item properties (_service_code)
      // Service codes are saved as hidden properties when items are added to cart
      let serviceTypes = [];

      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Only government items belong in this appointment. Wellness items are
        // scheduled post-checkout but still carry a _service_code line item
        // property (product-form.js), so including them would send the wrong
        // serviceTypes and can skip the HDS checks below via the OHC branch.
        // /cart.js has no metafields, so read the type off the rendered cart rows.
        const govtItemKeys = new Set(
          Array.from(document.querySelectorAll('.cart-items__table-row[data-product-type="government"]'))
            .map((row) => row.getAttribute('data-key'))
            .filter(Boolean)
        );

        // If no government rows are readable (unrendered or stale cart DOM), fall
        // back to the previous unfiltered sweep rather than sending an empty set.
        const canScopeToGovtItems = govtItemKeys.size > 0;

        // Collect service codes from line item properties
        const allServiceCodes = new Set();
        cart.items.forEach(item => {
          // The service fee has no cart row of its own (it renders in the footer),
          // so allow it through explicitly to keep the payload unchanged for
          // government-only carts.
          const isServiceFee = item.handle === 'salem-service-fee';
          if (canScopeToGovtItems && !isServiceFee && !govtItemKeys.has(item.key)) return;

          // Get service code from hidden line item property
          const serviceCode = item.properties?._service_code;
          if (serviceCode) {
            allServiceCodes.add(serviceCode);
          }
        });
        
        if (allServiceCodes.size > 0) {
          serviceTypes = Array.from(allServiceCodes);
        }
      } catch (e) {
        console.warn('Error fetching cart for service codes:', e);
      }
      
      // Fall back to product data service code if no cart items found
      if (serviceTypes.length === 0 && productData.serviceCode) {
        serviceTypes = [productData.serviceCode];
      }
      
      // 'SF' tells the booking API that a standard government service fee applies. It
      // used to be harvested from the fee's own line item, which tied this payload to
      // whether the customer is charged, so the fee could not be switched off without
      // changing what the API is told.
      //
      // It is now derived from the booking itself: a government item is being booked,
      // and serviceFeeApplies has not been explicitly cleared (only the upgrade path
      // clears it, since upgrades carry no fee). Deriving it from the cart rather than
      // from the flag alone matters in both directions. Several paths reset
      // booking_data wholesale when a journey starts (openNewCustomerForm in
      // cart-drawer.js, handleGovtClick here), which would otherwise drop 'SF'; and a
      // stale flag left over from an earlier government booking must not add 'SF' to a
      // later wellness one, or to a cart whose government item has since been removed.
      //
      // The rule lives in shouldSendServiceFeeCode() so this payload and the /slots
      // availedServices list cannot drift apart. Note it leans on the cart rows being
      // readable, the same condition as the scoping above. If they are not, this
      // branch omits 'SF' rather than guessing; the legacy fallback just below still
      // defaults an entirely empty list to ['SF'], unchanged from the original code.
      if (shouldSendServiceFeeCode() && !serviceTypes.includes('SF')) {
        serviceTypes.push('SF');
      }

      // Ensure we have at least one service type (default to SF)
      if (serviceTypes.length === 0) {
        serviceTypes = ['SF'];
      }

      // Service types priority: 1. MF, 2. OHC, 3. SF (default)
      let serviceTypePriority = '';
      if (serviceTypes.includes('MF')) {
        serviceTypePriority = 'MF';
      } else if (serviceTypes.includes('OHC')) {
        serviceTypePriority = 'OHC';
      } else if (serviceTypes.includes('PS')) {
        serviceTypePriority = 'PS';
      }

      // Get nationality code - if we have a code use it, otherwise look up by name
      let nationalityCode = parseInt(customerData.nationalityCode) || 0;
      if (nationalityCode === 0 && customerData.nationality) {
        // API returned nationality name instead of code, look it up
        const lookedUpCode = await this.getNationalityCodeByName(customerData.nationality);
        nationalityCode = parseInt(lookedUpCode) || 0;
      }
      
      // Encode mobile and email in base64
      const mobileEncoded = customerData.applicantMobile ? btoa(customerData.applicantMobile) : '';
      const emailEncoded = customerData.applicantEmail ? btoa(customerData.applicantEmail) : '';
      
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        "name": customerData.applicantName || '',
        "mobile": mobileEncoded,
        "dateOfBirth": dobISO,
        "email": emailEncoded,
        "gender": customerData.gender || '',
        "nationalityCode": nationalityCode,
        "emiratesIDNumber": emiratesIdEncoded,
        "passportNumber": passportEncoded,
        "clinicCode": bookingData.locationId || '',
        "appointmentDate": appointmentDateISO,
        "slotId": bookingData.appointmentTime || '',
        "serviceTypePriority": serviceTypePriority,
        "serviceTypes": serviceTypes
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      fetch(API_ENDPOINTS.SAVE_BOOKING, requestOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(async (result) => {
          // Get booking data for validation checks
          const bookingData = JSON.parse(localStorage.getItem('booking_data') || '{}');
          const isNewApplicant = bookingData.applicationType === 'new';
          
          // Check actual cart contents for government items (more reliable than localStorage)
          const govtItemsInCart = document.querySelectorAll('.cart-items__table-row[data-product-type="government"]');
          const isGovernmentProduct = govtItemsInCart.length > 0;
          
          // Check if HDS product is in cart by looking for the specific product ID
          const hdsProductInCart = document.querySelector('.cart-items__table-row[data-product-id="15660942721105"]');
          const isHDSProduct = hdsProductInCart !== null;
          const ohcProduct = serviceTypes.includes('OHC');
          
          // Check if user already has an MF application (only for government products)
          if (isGovernmentProduct && result?.getPackageFeeRes?.alreadyMFApplication && isNewApplicant) {
            // Clear cart, localStorage, and show warning
            await this.handleAlreadyMFApplication();
            reject(new Error('alreadyMFApplication'));
            return;
          }
          
          if(!ohcProduct) {
            // Check if new applicant selected a household service but API returns isHDS false
            // This means they already have an existing application
            if (isGovernmentProduct && isHDSProduct && result.getPackageFeeRes.isHDS === false) {
              // Clear cart, localStorage, and show warning - same as alreadyMFApplication
              await this.handleExistingHDSApplication();
              reject(new Error('existingHDSApplication'));
              return;
            }

            if (isGovernmentProduct && !isHDSProduct && result.getPackageFeeRes.isHDS === true) {
              // Clear cart, localStorage, and show warning - same as alreadyMFApplication
              await this.handleHDSClientBookingNonHDSProduct();
              reject(new Error('existingHDSApplication'));
              return;
            }
          }
          
          // Save the booking UID to localStorage
          if (result.uid) {
            localStorage.setItem('booking_uid', result.uid);
          }
          resolve(result);
        })
        .catch(async (error) => {
          console.error('Error saving booking:', error);
          // Handle API errors with same flow - clear cart, localStorage, show error
          await this.handleBookingError(
            Theme.translations?.booking?.booking_error_title || 'Booking Error',
            Theme.translations?.booking?.booking_error_message || 'The medical fitness details entered do not match GDFRA records. Please try again or contact our support team on +971 4260 4900.'
          );
          reject(error);
        });
    });
  }

  /**
   * Consolidated error handler for booking issues
   * Clears cart, localStorage, closes drawer, and shows error dialog
   * @param {string} title - Dialog title
   * @param {string} message - Dialog message
   */
  async handleBookingError(title, message) {
    // Clear all booking-related localStorage
    localStorage.removeItem('customerData');
    localStorage.removeItem('booking_data');
    localStorage.removeItem('booking_product');
    localStorage.removeItem('booking_uid');

    // Clear the cart and update the view
    try {
      // Get section ID before clearing
      const cartItemsComponent = document.querySelector('cart-items-component');
      const sectionId = cartItemsComponent?.dataset?.sectionId;
      
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Re-render the cart section to update the view
      if (sectionId) {
        const response = await fetch(`/?sections=${sectionId}`);
        const data = await response.json();
        
        if (data[sectionId]) {
          // Dispatch cart:update event with sections data
          // Note: component-cart-items expects detail.data.sections structure
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              itemCount: 0,
              source: 'booking-journey',
              data: {
                sections: { [sectionId]: data[sectionId] }
              }
            }
          }));
        }
      }

    } catch (error) {
      console.error('Error clearing cart:', error);
    }

    BookingButtonManager.enableAll();

    // Close the cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      const closeButton = cartDrawer.querySelector('button.close-button');
      if (closeButton) {
        closeButton.click();
      }
    }
    
    // Show warning dialog
    const cartBookingJourney = document.querySelector('cart-booking-journey');
    if (cartBookingJourney && typeof cartBookingJourney.showBookingErrorDialog === 'function') {
      cartBookingJourney.showBookingErrorDialog(title, message);
    }
  }

  /**
   * Handle the case where user already has an MF application
   */
  async handleAlreadyMFApplication() {
    await this.handleBookingError(
      Theme.translations?.booking?.mf_application_already_exists_title || 'Application Already Exists',
      Theme.translations?.booking?.mf_application_already_exists_message || 'Please use the "Existing Application" option to continue with your booking.'
    );
  }

  /**
   * Handle the case where new applicant has an existing HDS application
   */
  async handleExistingHDSApplication() {
    await this.handleBookingError(
      Theme.translations?.booking?.new_not_eligible_hds_title || 'Incorrect category selected',
      Theme.translations?.booking?.new_not_eligible_hds_message || 'Your visa category is not Household Domestic. Please go to \"Visa Medical Test\" and start again.'
    );
  }

  async handleHDSClientBookingNonHDSProduct() {
    await this.handleBookingError(
      Theme.translations?.booking?.new_not_eligible_non_hds_title || 'Incorrect category selected',
      Theme.translations?.booking?.new_not_eligible_non_hds_message || 'Your visa category is Household Domestic. Please go to "Household Domestic" and start again.'
    );
  }

  openBookingDrawer(event) {
    event.preventDefault();
    
    if (this.dialogComponent) {
      this.dialogComponent.open();
    }
  }

  storeCustomer() {
    
  }
  findCustomer() {

  }

  getCustomer() {
    if (localStorage.getItem('customer')) return localStorage.getItem('customer')
    return false;
  }


  initialiseForm() {

  }
}



if (!customElements.get('booking-journey')) {
  customElements.define('booking-journey', BookingJourney);
}


class BookingJourneyBtn extends Component {
  #abortController = new AbortController();

  constructor() {
    super();
    this.addEventListener('click', this.handleGovtClick.bind(this));
  }

  connectedCallback() {
    super.connectedCallback?.();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    const skeletonBtn = this.querySelector('.skeleton-btn');
    const btn = this.querySelector('.buy-buttons-block-booking');
    if(skeletonBtn) {
      setTimeout(() => {
        btn.style.visibility = 'visible';
        skeletonBtn.remove();
      }, 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController.abort();
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;
    if (!variant) return;

    const button = this.querySelector('button');
    if (!button) return;

    // Update variant ID and title
    button.dataset.variantId = variant.id;
    if (variant.title) {
      button.dataset.variantTitle = variant.title;
    }

    // Update disabled state based on availability
    if (variant.available) {
      button.removeAttribute('disabled');
    } else {
      button.setAttribute('disabled', '');
    }

    // Update price if present - variant.price is in cents
    const priceEl = button.querySelector('.price');
    if (priceEl && variant.price != null) {
      const price = variant.price / 100;
      // Remove .00 if it's a whole number
      const formattedPrice = price % 1 === 0 ? price.toString() : price.toFixed(2);
      priceEl.textContent = formattedPrice;
    }
  };

  handleGovtClick(event) {
    event.preventDefault();
    
    // Get product data from button attributes
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    
    const productName = button.dataset.productName;
    const variantId = button.dataset.variantId;
    const variantTitle = button.dataset.variantTitle || '';
    const productId = button.dataset.productId;
    const productType = button.dataset.productType || '';
    const serviceCode = button.dataset.serviceCode;
    
    if (productName && variantId && productId) {
      // Save product data to localStorage (replaces existing)
      const productData = {
        name: productName,
        variantId: variantId,
        variantTitle: variantTitle,
        productId: productId,
        productType: productType,
        serviceCode: serviceCode || '',
        timestamp: Date.now()
      };

      localStorage.setItem('booking_product', JSON.stringify(productData));
      
      // Update product name display if element exists
      const productNameEl = document.querySelector('[data-product-name]');
      if (productNameEl) {
        productNameEl.innerText = productName;
      }
    }
    
    // Save full Step 1 state BEFORE opening the cart drawer.
    // This prevents normalizeStateOnRestore() from clobbering our intended state
    // during the synchronous cartDrawer.open() → restoreBookingState() call chain.
    try {
      const currentBookingData = localStorage.getItem('booking_data');
      const currentState = currentBookingData ? JSON.parse(currentBookingData) : {};
      const previousStep = currentState.step || 2; // Default to Step 2 if no previous state

      // Write the complete Step 1 state with previousStep for close-and-reopen restoration.
      // serviceFeeApplies is carried over: shouldSendServiceFeeCode() treats a missing
      // flag as "send SF", so wiping an explicit false here would re-attach the fee
      // code to an upgrade booking that was never charged one. (undefined is dropped
      // by JSON.stringify, so the standard case stays a clean reset.)
      localStorage.setItem('booking_data', JSON.stringify({
        step: 1,
        applicationType: 'existing',
        panel: 1,
        previousStep: previousStep,
        serviceFeeApplies: currentState.serviceFeeApplies
      }));
    } catch (e) {
      console.error('Error saving booking state for Step 1:', e);
    }

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      // Skip normalizeStateOnRestore — we've already set the correct state above
      BookingStateManager._skipNormalize = true;
      cartDrawer.open();
      BookingStateManager._skipNormalize = false;

      const cartDrawerInner = document.querySelector('.cart-drawer__inner');
      if (cartDrawerInner) {
        cartDrawerInner.dataset.step = '1';
        cartDrawerInner.dataset.panel = '1';
      }

      // Notify cart-booking-journey to update its state and buttons
      const cartBookingJourney = document.querySelector('cart-booking-journey');
      console.log('[DEBUG handleGovtClick]', { found: !!cartBookingJourney, isConnected: cartBookingJourney?.isConnected });
      if (cartBookingJourney) {
        // Sync in-memory state to match localStorage
        cartBookingJourney.currentPanel = 1;
        cartBookingJourney.selectedOption = 'existing';

        // Update DOM and button states
        if (typeof cartBookingJourney.manageBookingStates === 'function') {
          cartBookingJourney.manageBookingStates();
        }
      }
    }
  }
}

if (!customElements.get('booking-journey-btn')) {
  customElements.define('booking-journey-btn', BookingJourneyBtn);
}

